/**
 * CASTLE ATLAS — Precision Storytelling Landing
 * ─────────────────────────────────────────────
 * Pattern matches the current CopywritingLanding.jsx: usePageTitle + useProgress,
 * no JS dark-mode tracking. Theming is entirely CSS-driven (global `.dark` on
 * <html>): the cloud tiles use `--cloud-*`, everything else — including the
 * hero, which no longer carries its own day/night sky gradient (removed in
 * v3; it now blends into the same background as the rest of the page) —
 * uses the platform hsl(var(--*)) tokens, so both light and dark are legible
 * without an isDark observer.
 *
 * Progress is read like PromptLanding: filter user_progress rows by lesson_id
 * membership (no skill_id column). Storytelling owns lessons 4–8 (../modules.js).
 *
 * Radar data source == Copywriting's radar: per-MODULE completion % (done/reps).
 * user_progress has a single `score` column, not a per-rubric-axis breakdown, so
 * there is no honest per-axis quality data to plot — the axes are the 5 modules
 * (which map 1:1 to the rubric), exactly as Copywriting labels its axes by module.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@hooks/usePageTitle';
import { useProgress } from '@hooks/useProgress';
import SkillLoadingScreen from '@components/SkillLoadingScreen';
import StampBadge from '@components/StampBadge';
import { CASTLE_ICON_DAY, CASTLE_ICON_NIGHT } from '../castleIconData.js';
import { ArrowRight, Scissors, CheckCircle2, Layers, BarChart3 } from 'lucide-react';
import { STORYTELLING_MODULES, STORYTELLING_LESSON_IDS, SKILL_ROUTE } from '../modules.js';
import { MODULES } from '../data/storytellingExercises.js';

const ACCENT = '#db2777';
const RUBRIC = ['Structural completeness', 'Economy', 'Specificity', 'Payoff', 'Audience fit'];
const shortTitle = (t) => t.replace(/^The\s+/i, '');

// Per-module CTA copy — keyed by slug (matching STORYTELLING_MODULES)
// rather than by index/position, so adding a locked state later is just a
// new field on the same lookup, not a restructure. Five buttons all
// saying "Start" read as dead; each module gets its own verb for "not
// started yet" and "already in progress" (switched on that module's own
// exercise count, not the page-level progress).
const MODULE_CTA = {
  'origin-cut':    { notStarted: 'Make the cut',   inProgress: 'Keep cutting' },
  'turning-point': { notStarted: 'Find the turn',  inProgress: 'Keep turning' },
  'data-story':    { notStarted: 'Add the weight', inProgress: 'Keep building' },
  'pitch-arc':     { notStarted: 'Take the shot',  inProgress: 'Keep aiming' },
  'callback':      { notStarted: 'Land the echo',  inProgress: 'Keep it ringing' },
};

/* Live-edit subheading tokens. Trailing-space convention: joining every token's
   text reads as a rambling first draft; joining only the { c:false } tokens
   reads as EXACTLY the tight final copy:
   "Not creative writing — ruthless editing. Five modules of cutting rambling raw
    material down to the least detail that still lands the point, scored in real
    time by AI."
   The { c:true } filler tokens strike through (rose) then collapse on load, so
   the page visibly performs the ruthless edit it teaches. Filip can retune the
   wording/cadence live. */
const SUBHEAD_TOKENS = [
  { t: "Okay so, here's the idea: ", c: true },
  { t: 'Not ', c: false }, { t: 'creative ', c: false }, { t: 'writing ', c: false },
  { t: 'or more like just ', c: true },
  { t: '— ', c: false }, { t: 'ruthless ', c: false }, { t: 'editing. ', c: false },
  { t: "So there's ", c: true },
  { t: 'Five ', c: false }, { t: 'modules ', c: false },
  { t: 'or sections I guess, ', c: true },
  { t: 'of ', c: false }, { t: 'cutting ', c: false }, { t: 'rambling ', c: false }, { t: 'raw ', c: false }, { t: 'material ', c: false },
  { t: "and taking out the stuff you don't really need, ", c: true },
  { t: 'down ', c: false }, { t: 'to ', c: false }, { t: 'the ', c: false }, { t: 'least ', c: false }, { t: 'detail ', c: false },
  { t: 'or really the smallest bit ', c: true },
  { t: 'that ', c: false }, { t: 'still ', c: false }, { t: 'lands ', c: false }, { t: 'the ', c: false }, { t: 'point, ', c: false },
  { t: "you know, the one you're trying to make, and it's ", c: true },
  { t: 'scored ', c: false }, { t: 'in ', c: false }, { t: 'real ', c: false }, { t: 'time ', c: false },
  { t: 'right there, ', c: true },
  { t: 'by ', c: false }, { t: 'AI.', c: false },
  { t: " It's honestly pretty cool.", c: true },
];
const SUBHEAD_CUTS = SUBHEAD_TOKENS.map((tok, i) => (tok.c ? i : -1)).filter((i) => i >= 0);

/* Five distinct cloud silhouettes (viewBox 0 0 240 120) — different lobe counts
   and puffiness so they read as five different clouds, not one shape ×5. */
const CLOUD_PATHS = [
  // 0 — Origin Cut: 4 balanced lobes
  'M46,112 Q20,112 20,90 Q8,72 30,66 Q34,42 62,46 Q74,22 102,32 Q120,12 146,28 Q170,18 182,42 Q212,40 214,66 Q232,72 220,90 Q222,112 196,112 Z',
  // 1 — Turning Point: 3 tall lobes, one dominant
  'M50,113 Q22,113 22,86 Q10,64 34,60 Q40,28 76,40 Q98,8 130,28 Q162,12 178,44 Q212,42 210,70 Q226,76 212,92 Q214,113 188,113 Z',
  // 2 — Data Story: 5 small wavy lobes
  'M42,112 Q18,112 20,92 Q8,78 26,72 Q26,54 46,56 Q52,36 74,44 Q84,24 106,34 Q120,18 140,32 Q158,22 172,40 Q196,38 200,60 Q220,64 214,86 Q220,112 194,112 Z',
  // 3 — Pitch Arc: wide, flatter, low bumps
  'M40,110 Q16,110 18,92 Q10,78 30,74 Q38,58 64,62 Q80,44 108,52 Q132,40 156,54 Q182,48 192,68 Q216,70 210,90 Q214,110 188,110 Z',
  // 4 — Callback: asymmetric, big left lobe
  'M48,113 Q22,113 24,88 Q12,68 36,64 Q42,34 80,46 Q94,12 126,30 Q152,16 170,42 Q206,40 204,72 Q222,78 208,94 Q210,113 184,113 Z',
];

/* ============================================================
   1. ARC-SHAPED PROGRESS METER — thin-line style
   Matches the reference "YOUR STORY ARC" mockup: a thin stroke, a wide
   shallow radius, one marker at the current position, and butt (not
   round) line caps so the ends read as a clean line rather than blunt
   circles.
   v3 refinements:
     - Sweep narrowed 100°→95° ("5° straighter" — flatter curvature).
     - The old dynamic "continuation chevron" (positioned relative to the
       current-progress point) is gone; a fixed arrowhead now sits at the
       arc's true 100% end, always in the same place regardless of
       progress — the current-position dot effectively covers the start
       end already (it sits exactly there at 0% progress), so the two
       ends now each read distinctly: dot at start, arrowhead at finish.
     - 5 small, subtle per-module position dots are back (much smaller
       than the thick segmented-circle version from two passes ago,
       which this replaces) — just a faint filled/hollow dot per module
       at its position along the arc, independent of the continuous
       %-fill.
   ============================================================ */
const ArcProgress = ({ pct, moduleStates }) => {
  // v4: doubled — every geometry constant below scales 2x uniformly
  // (cx/cy/radius/stroke/size/height, arrow + dot + marker proportions,
  // label font sizes) so the whole graphic enlarges as one coherent shape
  // rather than a bigger ring with unchanged-size decoration on it.
  // v5: stroke thinned back down (5→2); height cropped (380→250) since the
  // label block below now sits much higher, leaving the old lower portion
  // of the viewBox empty — cropping it is what actually tightens the
  // vertical gap to the "Keep cutting..." caption after the svg, not just
  // moving the text.
  // v7: inverted cap (∩) to bowl (∪) so the castle can nest into the dip
  // instead of colliding with it — cx/cy/radius unchanged (so the x-range
  // and horizontal alignment with the other hero columns is untouched),
  // only startAngle/endAngle/sweep-flag flip which half of the circle gets
  // drawn. Empirically verified (standalone sweep-flag test, all 4
  // start/end/sweep combinations rendered side by side): 137.5°→42.5° with
  // sweep-flag 0 is the only combination whose drawn path matches the
  // pointAt()-computed dot/arrow positions AND keeps frac=0 on the left,
  // frac=1 on the right. viewBox is now a cropped WINDOW (min-y 430, not
  // 0) into this same cx/cy/radius coordinate space — the bowl's own
  // content (ends ~y444, dip y540) sits far below where the old cap's
  // content did (y20-104), so the crop moved with it rather than growing
  // to include both.
  const cx = 280, cy = 280, radius = 260, stroke = 2;
  const size = 560, viewBoxMinY = 430, height = 120;

  const startAngle = 137.5 * (Math.PI / 180);
  const endAngle = 42.5 * (Math.PI / 180);
  const sweepDeg = 95;
  const largeArc = 0;
  const sweepFlag = 0;
  const pointAt = (frac) => {
    const a = startAngle + frac * (endAngle - startAngle);
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a), a];
  };

  const [x1, y1] = pointAt(0);
  const [x2, y2, endA] = pointAt(1);
  const trackD = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} ${sweepFlag} ${x2} ${y2}`;
  const arcLen = (2 * Math.PI * radius) * (sweepDeg / 360);

  const clampedPct = Math.min(Math.max(pct, 0), 100);
  const fillLen = arcLen * (clampedPct / 100);

  // Fixed arrowhead at the arc's true end (100%), oriented along the
  // tangent there — replaces the old progress-relative chevron.
  // v5: detached from the line's actual end — the whole arrowhead shape
  // (tip included) is pushed further out along the tangent by `gap` so
  // there's a visible break, rather than the tip touching (x2,y2) exactly.
  // v8: sign fixed. (-sin a, cos a) is the tangent in the direction of
  // INCREASING angle — correct for the old cap shape, where frac (and the
  // direction of travel) increased WITH angle. The v7 bowl runs the other
  // way: frac increases as angle DECREASES (start 137.5° -> end 42.5°), so
  // the forward tangent is the negation, (sin a, -cos a). Carrying the old
  // sign over pointed the arrowhead back into the curve instead of
  // outward/upward continuing past it.
  const etx = Math.sin(endA), ety = -Math.cos(endA);
  const gap = 10;
  const tipX = x2 + etx * gap, tipY = y2 + ety * gap;
  const arrowBackX = tipX - etx * 18, arrowBackY = tipY - ety * 18;
  const arrowD = `M ${arrowBackX - ety * 8} ${arrowBackY + etx * 8} L ${tipX} ${tipY} L ${arrowBackX + ety * 8} ${arrowBackY - etx * 8}`;

  // Milestone dots — v6: exactly 4, one per completion point of modules
  // 1-4 (Origin Cut through Pitch Arc), at fractions 1/5..4/5 along the
  // arc — not one per module including the arc's own start/end. The
  // first dot marks completing module 1, not the start of the journey;
  // the 5th module (Callback) gets no dot of its own — its completion IS
  // the arc's true end, already marked by the arrowhead. Net result:
  // bare line -> 4 dots -> arrow, no marker at either end of the stroke.
  const moduleDots = (moduleStates || []).slice(0, 4).map((done, i) => {
    const [dx, dy] = pointAt((i + 1) / 5);
    return { dx, dy, done };
  });

  // v11: castle geometry derived from the arc's own computed values, not
  // hand-tuned against the column separately — one source of truth.
  // Change the angles/radius above and this recomputes automatically
  // instead of needing a second, independent re-tuning pass the way the
  // old CSS % width and the arc's viewBox used to.
  //   - CASTLE_CHORD_FRACTION: the castle's base spans this fraction of
  //     the chord between the arc's own two endpoints (x2-x1) — the
  //     visual reference for "fitted to the bowl" is that gap, not the
  //     viewBox's full width (which includes empty margin past both ends).
  //   - dipXFrac: the dip's x position (pointAt(0.5)) as a fraction of
  //     this container's own WIDTH, so the castle's `left` percentage is
  //     computed the same way — against this component's own rendered
  //     geometry, not the column it happens to sit in.
  const CASTLE_CHORD_FRACTION = 0.69;
  const CASTLE_ASPECT = 0.87;
  const SEAT_GAP_PX = 5;
  const [dipX, dipY] = pointAt(0.5);
  const chordWidth = x2 - x1;
  const castleWidthLocal = chordWidth * CASTLE_CHORD_FRACTION;
  const castleWidthFrac = castleWidthLocal / size;
  const dipXFrac = dipX / size;

  // v12: the container's height used to be just the arc's own thin slice
  // (aspect-ratio size/height) — correct for the arc itself, but the
  // castle is ~2.5x taller than that slice and sat mostly ABOVE the
  // container via overflow:visible. An absolutely positioned child never
  // contributes to its parent's layout size, so document flow only ever
  // reserved room for the thin slice — in the 3-column desktop grid nothing
  // else was up there to collide with, but stacked (mobile/tablet) the
  // castle's overflow reached up far enough to paint over the stats row
  // above it, which is exactly the "container's reported height doesn't
  // match its true visual footprint" bug the v11 rewrite was supposed to
  // fix and didn't, for the TOP edge specifically (v11 only checked the
  // bottom edge, against the note card).
  // Fix: size the container to the castle's actual full extent — from the
  // castle's own top down to the arc's own bottom (the lowest point either
  // one reaches) — computed in the same local viewBox units as everything
  // else here, so it's still one source of truth rather than a second
  // independent constant. The arc's own thin slice and the dots overlay
  // then anchor to the BOTTOM of this taller box at a computed height
  // fraction, instead of filling it, so their geometry inside the svg
  // viewBox is unaffected — only where that slice sits within the now-
  // taller container changes.
  const castleHeightLocal = castleWidthLocal / CASTLE_ASPECT;
  const arcBottomLocal = viewBoxMinY + height;
  const castleTopLocal = dipY - castleHeightLocal;
  const containerTopLocal = Math.min(viewBoxMinY, castleTopLocal);
  const containerHeightLocal = arcBottomLocal - containerTopLocal;
  const arcSliceHeightFrac = height / containerHeightLocal;
  const castleBottomFromContainerBottomFrac = (arcBottomLocal - dipY) / containerHeightLocal;

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {/* v11: castle + track + dots now share ONE positioned container
          with a real, definite size (aspect-ratio derived from this same
          viewBox, not "however tall the in-flow svg happens to render") —
          all three are absolutely-positioned layers inside it, sized/
          placed as percentages of that one shared box. Previously the
          castle sat in a separate sibling div BEFORE this one, seated via
          a negative margin-bottom pulling THIS container up underneath
          it — which also pulls every subsequent flow sibling up by the
          same amount.
          v12: the container's own height now spans the castle's full
          extent, not just the arc's thin slice (see castleHeightLocal
          etc. above) — its reported outer height matches what's actually
          painted, top to bottom, so normal flow spacing on both sides of
          it is correct without needing overflow to reach into a
          neighboring section's space in either direction. */}
      <div style={{ position: 'relative', width: '100%', maxWidth: size, aspectRatio: `${size} / ${containerHeightLocal}` }}>
        {/* width:100%/maxWidth (not a fixed pixel width) so the doubled
            graphic scales down on narrow viewports instead of overflowing —
            viewBox keeps every coordinate above correct at any rendered
            size, same responsive pattern SketchRadar already uses. */}
        <svg
          viewBox={`0 ${viewBoxMinY} ${size} ${height}`}
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${(arcSliceHeightFrac * 100).toFixed(3)}%`, display: 'block', overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={ACCENT} stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f472b6" stopOpacity="0.9" />
            </linearGradient>
          </defs>

          {/* Track — thin, butt caps (not round) so the ends read as a
              clean line rather than a blunt circle. */}
          <path d={trackD} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} strokeLinecap="butt" />
          {/* Fill — thin colored line up to the current percentage. */}
          <path
            d={trackD}
            fill="none"
            stroke="url(#arcGrad)"
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeDasharray={arcLen}
            strokeDashoffset={arcLen - fillLen}
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)' }}
          />

          {/* Fixed arrowhead at the true 100% end. */}
          <path
            d={arrowD}
            fill="none"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth={3.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.6"
          />
        </svg>

        {/* Castle — absolutely positioned within the same box the track
            svg above occupies, so `left`/`bottom` percentages resolve
            against that box's real width/height (given by aspect-ratio on
            the shared container, not "auto"). z-index:2 sits below the
            dots svg's z-index:3 (below), same as before, so the castle
            still paints OVER the track but UNDER the always-visible dots. */}
        <div
          className="story-castle-box"
          aria-hidden="true"
          style={{
            position: 'absolute',
            width: `clamp(100px, ${(castleWidthFrac * 100).toFixed(2)}%, 250px)`,
            left: `${(dipXFrac * 100).toFixed(2)}%`,
            bottom: `calc(${(castleBottomFromContainerBottomFrac * 100).toFixed(3)}% + ${SEAT_GAP_PX}px)`,
            transform: 'translateX(-50%)',
            aspectRatio: CASTLE_ASPECT,
            zIndex: 2,
          }}
        >
          {/* fetchPriority (not a preload link): route-specific, so a global
              index.html preload would fetch it on every page. The parent's
              own aspectRatio (above) already reserves this box's space
              before either image decodes — no shift either way — this just
              gets the bytes moving sooner instead of at default priority. */}
          <img
            src="/precision-storytelling/castle-day.png"
            alt=""
            className="story-castle-day story-castle-img"
            fetchPriority="high"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <img
            src="/precision-storytelling/castle-night.png"
            alt=""
            className="story-castle-night story-castle-img"
            fetchPriority="high"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        {/* Milestone dots — separate overlay svg, positioned to exactly
            cover the track svg above (same viewBox) but painted at
            z-index:3, above the castle's z-index:2, so they stay visible
            regardless of how much of the track underneath the castle
            covers. v5: always fully visible regardless of completion (the
            old hollow variant used stroke:hsl(var(--border)), a hairline
            color deliberately low-contrast against the page background by
            design elsewhere, which made incomplete dots nearly invisible).
            Uses muted-foreground (a legible, always-visible tone) for the
            incomplete stroke. v6: halo grown 7->11 — the v5 gap read as
            still-attached at a glance; this is a clearer break between the
            dot and the stroke it sits near. */}
        <svg
          viewBox={`0 ${viewBoxMinY} ${size} ${height}`}
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${(arcSliceHeightFrac * 100).toFixed(3)}%`, zIndex: 3, overflow: 'visible', pointerEvents: 'none' }}
        >
          {moduleDots.map(({ dx, dy, done }, i) => (
            <g key={i}>
              <circle cx={dx} cy={dy} r={11} fill="hsl(var(--background))" />
              <circle
                cx={dx}
                cy={dy}
                r={4.5}
                fill={done ? ACCENT : 'hsl(var(--background))'}
                stroke={done ? 'none' : 'hsl(var(--muted-foreground))'}
                strokeWidth={done ? 0 : 2}
              />
            </g>
          ))}
        </svg>
      </div>

      {/* Labels — v7: moved out of the SVG into plain flow (was <text> at
          y=cy-90/cy-65, positions tuned for the old cap shape's peak).
          Now that the arc is a bowl, its lowest point (where the castle
          sits) is the LAST thing before these in document order, so plain
          flow already puts the labels below the whole castle+arc
          assembly for free — no coordinate math needed. */}
      <div
        style={{
          marginTop: 6,
          fontFamily: 'var(--font-skill-story)',
          fontSize: 34,
          fontWeight: 700,
          color: 'hsl(var(--foreground))',
          lineHeight: 1,
        }}
      >
        {pct}
        <span style={{ fontSize: 18 }}>%</span>
      </div>
      <div
        style={{
          marginTop: 2,
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        Arc Progress
      </div>

      <p
        style={{
          marginTop: 2,
          fontSize: 13,
          fontStyle: 'italic',
          color: ACCENT,
          fontFamily: 'var(--font-script)',
        }}
      >
        Keep cutting. Clarity is the climax.
      </p>
    </div>
  );
};

/* ============================================================
   2. CONCEPT DIAGRAMS — delicate hairline linework
   v4: redesigned to match Filip's reference exactly — hairline strokes
   (~1px, not the old 1.6-1.8), fine short dashes, predominantly dark ink
   with exactly ONE small rose-pink accent element per diagram, no filled
   shapes except light diagonal hatching (Pitch Arc's intersection — real
   hatch lines clipped to the lens shape, not a solid accent-tint fill
   like the old version used), no background panel, generous whitespace,
   fluid rather than bold/geometric. Kept the per-slug lookup + 176px
   display size (viewBox stays "0 0 88 88") from v3.
   ============================================================ */
const CONCEPT_DIAGRAM_SIZE = 176;
const CONCEPT_DIAGRAM_INK = 'hsl(var(--foreground))';

// Data Story diagram — named so the peak/baseline/start positions are
// editable without hunting through the SVG. The dotted drop-lines and dots
// below all derive from these, so moving e.g. PEAK_X moves its dot and its
// drop-line together.
const DATA_STORY_PEAK_X = 52;
const DATA_STORY_PEAK_Y = 26;
const DATA_STORY_START_X = 10;
const DATA_STORY_START_Y = 60;
const DATA_STORY_BASELINE_Y = 76;
const DATA_STORY_BASELINE_X1 = 9.5;
const DATA_STORY_BASELINE_X2 = 82;

const CONCEPT_DIAGRAMS = [
  {
    slug: 'origin-cut',
    caption: 'Remove everything that doesn’t serve the story.',
    svg: (
      <svg width={CONCEPT_DIAGRAM_SIZE} height={CONCEPT_DIAGRAM_SIZE} viewBox="0 0 88 88">
        {/* Loose scatter of dots at left — v8: v7's cluster came out as an
            unreadable clump (too many dots, too tight). Back to a sparse
            "constellation" — fewer dots, real spacing between them so
            each one reads individually, threaded together with dashed
            connector lines rather than implied by density. Two of the
            nine are accent pink for visual interest. Kept level with the
            dashed circle (cy=46, v6's fix). */}
        {[
          [3, 44], [7, 38], [11, 45], [14, 39], [23, 46], [6, 51], [20, 51],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="1.4" fill={CONCEPT_DIAGRAM_INK} opacity="0.55" />
        ))}
        {[[19, 42], [10, 53]].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="1.4" fill={ACCENT} opacity="0.85" />
        ))}
        {/* Dashed constellation lines threading the dots together */}
        <path d="M3,44 L7,38" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.3" />
        <path d="M7,38 L11,45" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.3" />
        <path d="M11,45 L14,39" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.3" />
        <path d="M14,39 L19,42" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.3" />
        <path d="M19,42 L23,46" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.3" />
        <path d="M6,51 L10,53" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.26" />
        <path d="M10,53 L20,51" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.26" />
        {/* Resolving rightward into a single dashed circle — v6: straight
            and horizontal (was a descending curve, M23,23 -> 60,44) now
            that the scatter sits level with the circle's own y=46. */}
        <path d="M23,46 L60,46" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="1.2 2" opacity="0.4" />
        <circle cx="71" cy="46" r="9" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="2 2" opacity="0.6" />
        <circle cx="68" cy="46" r="1.9" fill={ACCENT} />
        <circle cx="74.5" cy="46" r="1.6" fill={CONCEPT_DIAGRAM_INK} opacity="0.7" />
      </svg>
    ),
  },
  {
    slug: 'turning-point',
    caption: 'Build the spine. Order creates meaning.',
    svg: (
      <svg width={CONCEPT_DIAGRAM_SIZE} height={CONCEPT_DIAGRAM_SIZE} viewBox="0 0 88 88">
        {/* Horizontal hairline with small solid dots spaced along it — v5:
            thinned overall (was reading thick/clunky next to the other
            four) and the 3 small circles switched from hollow rings
            (whose 1px stroke was a large fraction of their tiny radius,
            reading chunky) to small solid dots, matching the other
            diagrams' dot convention. */}
        <line x1="8" y1="44" x2="80" y2="44" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="0.75" opacity="0.3" />
        {[18, 30, 58].map((x, i) => (
          <circle key={i} cx={x} cy="44" r="1.5" fill={CONCEPT_DIAGRAM_INK} opacity="0.45" />
        ))}
        {/* One emphasized pink dot at center, ringed by a dashed circle */}
        <circle cx="44" cy="44" r="7.5" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="0.9" strokeDasharray="2 2" opacity="0.55" />
        <circle cx="44" cy="44" r="2.4" fill={ACCENT} />
        {/* Small pink dot terminates the line at right */}
        <circle cx="79" cy="44" r="1.8" fill={ACCENT} />
      </svg>
    ),
  },
  {
    slug: 'data-story',
    caption: 'Add tension, contrast and emotional weight.',
    svg: (
      <svg width={CONCEPT_DIAGRAM_SIZE} height={CONCEPT_DIAGRAM_SIZE} viewBox="0 0 88 88">
        {/* Dashed curve rising to a peak and falling */}
        <path
          d="M10,60 Q24,58 32,44 Q40,28 50,26 Q62,24 70,40 Q76,48 80,54"
          fill="none"
          stroke={CONCEPT_DIAGRAM_INK}
          strokeWidth="1"
          strokeDasharray="2 2"
          opacity="0.55"
        />
        {/* Implied baseline + thin dotted drop-lines from the key points —
            derived from the named constants above so moving the peak or
            the baseline moves its drop-line with it. */}
        <line x1={DATA_STORY_BASELINE_X1} y1={DATA_STORY_BASELINE_Y} x2={DATA_STORY_BASELINE_X2} y2={DATA_STORY_BASELINE_Y} stroke={CONCEPT_DIAGRAM_INK} strokeWidth="0.75" strokeDasharray="1 2.5" opacity="0.22" />
        <line x1={DATA_STORY_START_X} y1={DATA_STORY_START_Y} x2={DATA_STORY_START_X} y2={DATA_STORY_BASELINE_Y} stroke={CONCEPT_DIAGRAM_INK} strokeWidth="0.75" strokeDasharray="1 2" opacity="0.4" />
        <line x1={DATA_STORY_PEAK_X} y1={DATA_STORY_PEAK_Y} x2={DATA_STORY_PEAK_X} y2={DATA_STORY_BASELINE_Y} stroke={CONCEPT_DIAGRAM_INK} strokeWidth="0.75" strokeDasharray="1 2" opacity="0.4" />
        {/* Pink dot at the low start, dark dot at the peak */}
        <circle cx={DATA_STORY_START_X} cy={DATA_STORY_START_Y} r="2" fill={ACCENT} />
        <circle cx={DATA_STORY_PEAK_X} cy={DATA_STORY_PEAK_Y} r="2" fill={CONCEPT_DIAGRAM_INK} opacity="0.75" />
      </svg>
    ),
  },
  {
    slug: 'pitch-arc',
    caption: 'Clarity turns complexity into connection.',
    svg: (
      <svg width={CONCEPT_DIAGRAM_SIZE} height={CONCEPT_DIAGRAM_SIZE} viewBox="0 0 88 88">
        <defs>
          {/* Same vesica shape as the two circles' intersection, used only
              to clip the hatch lines below to that lens region. v5 fix:
              the true intersection points of two r=20 circles centered 24
              apart (cx=32, cx=56) are at y = 44 ± sqrt(20² - 12²) = 44±16,
              i.e. (44,28) and (44,60) — this previously used (44,24) and
              (44,64) (a 40-tall chord, matching r as a diameter instead of
              the real ~32-tall chord), which clipped a taller lens than
              the actual overlap, reading as offset over one circle rather
              than centered in the shared middle. */}
          <clipPath id="pitchArcLensClip">
            <path d="M 44 28 A 20 20 0 0 1 44 60 A 20 20 0 0 1 44 28" />
          </clipPath>
        </defs>
        {/* Two overlapping hairline circles */}
        <circle cx="32" cy="44" r="20" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" opacity="0.55" />
        <circle cx="56" cy="44" r="20" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" opacity="0.55" />
        {/* Intersection filled with fine diagonal pink hatching, not a solid
            tint — v6: doubled 8->16 lines (spacing halved 3.4->1.7 to keep
            the same span) since the placement is right now but read too
            sparse. v7: 16->19 lines, starting x0 nudged 30->28 (same 1.7
            step) — at 16 lines the family of parallel lines undershot the
            lens on the near side, leaving a bare sliver uninked at the
            bottom-left tip; verified empirically that 19 lines from x0=28
            reaches full edge-to-edge coverage with no gap top or bottom. */}
        <g clipPath="url(#pitchArcLensClip)" opacity="0.55">
          {Array.from({ length: 19 }).map((_, i) => {
            const x = 28 + i * 1.7;
            return <line key={i} x1={x} y1="18" x2={x - 16} y2="70" stroke={ACCENT} strokeWidth="0.8" />;
          })}
        </g>
      </svg>
    ),
  },
  {
    slug: 'callback',
    caption: 'End with resonance. Make it memorable.',
    svg: (
      <svg width={CONCEPT_DIAGRAM_SIZE} height={CONCEPT_DIAGRAM_SIZE} viewBox="0 0 88 88">
        {/* Concentric hairline circles — outer two dashed, inner solid */}
        <circle cx="44" cy="44" r="34" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="2 3" opacity="0.3" />
        <circle cx="44" cy="44" r="23" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" strokeDasharray="2 3" opacity="0.45" />
        <circle cx="44" cy="44" r="12" fill="none" stroke={CONCEPT_DIAGRAM_INK} strokeWidth="1" opacity="0.6" />
        {/* Single pink dot dead center */}
        <circle cx="44" cy="44" r="2.4" fill={ACCENT} />
      </svg>
    ),
  },
];

const ConceptDiagram = ({ slug }) => {
  const d = CONCEPT_DIAGRAMS.find((x) => x.slug === slug);
  if (!d) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      {d.svg}
      <p style={{ fontSize: 12, lineHeight: 1.5, color: 'hsl(var(--muted-foreground))', fontWeight: 500, maxWidth: 200 }}>
        {d.caption}
      </p>
    </div>
  );
};

/* ── Spool — sits before the first tile in the module row. Wired for
   spool-day.png/spool-night.png (day/night pair, CSS-only .dark swap —
   same pattern as the character art), both live now (real flat art, ratio
   ~1.1). v6: the connecting thread between tiles is gone entirely (per
   Filip — several rounds of attempts never landed convincingly, not worth
   a sixth) — the spool now stands alone as a decorative element at the
   head of the row rather than a visual "source" the thread used to spool
   out from. Sized to roughly a third of a character tile (tiles are
   255-300px square) rather than the old thread-era 54px icon size. A
   failed image load just renders nothing (empty reserved-width slot). ── */
const ThreadSpool = () => {
  const [artFailed, setArtFailed] = useState(false);
  return (
    <div
      aria-hidden="true"
      className="story-spool-slot"
    >
      {!artFailed && (
        <>
          <img
            src="/precision-storytelling/spool-day.png"
            alt=""
            className="story-spool-day story-spool-img"
            onError={() => setArtFailed(true)}
          />
          <img
            src="/precision-storytelling/spool-night.png"
            alt=""
            className="story-spool-night story-spool-img"
            onError={() => setArtFailed(true)}
          />
        </>
      )}
    </div>
  );
};

/* ============================================================
   4. RADAR CHART — softer gradient styling
   ============================================================ */
const JITTER_X = [1.1, -1.6, 1.3, -1.0, 1.5];
const JITTER_Y = [-1.4, 1.2, -1.1, 1.6, -1.3];
const vertex = (i, frac, cx, cy, r) => {
  const a = -Math.PI / 2 + i * ((2 * Math.PI) / 5);
  // v6: floor raised 0.035->0.09 — at real 0% (frac=0 on every axis) the
  // old floor put all 5 vertices within ~3px of center, closer together
  // than the r=3 data-dot markers' own diameter, so they visually stacked
  // into one blob rather than reading as 5 distinct points ("looks like a
  // rendering bug" at a glance). 0.09 keeps them clearly separated (chord
  // between adjacent vertices > dot diameter) while still reading as a
  // small, legible "just started" pentagon rather than a collapsed point.
  const rad = r * Math.max(frac, 0.09);
  return [cx + Math.cos(a) * rad + JITTER_X[i % 5], cy + Math.sin(a) * rad + JITTER_Y[i % 5], a];
};
const poly = (pts) => `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)} ` + pts.slice(1).map((p) => `L${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ') + ' Z';

// v11: the "always render, never gated" policy below is now specifically
// for any REAL value — it doesn't cover genuine zero. At true 0% on every
// axis this floor is exactly the problem Sales & Negotiation's reference
// avoids: with no data yet, it renders axes/grid/labels only and skips the
// data shape entirely, rather than drawing a degenerate floor-sized
// pentagon that reads as "a cluster of dots" at a glance. SketchRadar
// checks for that all-zero case itself (see `allZero` below) and skips the
// polygon/vertices/center entirely when it's true; the moment any single
// axis has a real value, this floor is what keeps that shape legible
// rather than a collapsed point, exactly as it did before.
//
// Historical note (pre-round-11): none of the reference mockups hid the
// radar behind a locked placeholder — every one showed the actual filled
// polygon, even at low/partial values ("Your progress" cards render the
// shape plainly). Matching that: the shape always renders once there's ANY
// data, never gated behind a "cut a few reps to unlock" overlay (the
// vertex() minimum-radius floor already keeps it a
// legible pentagon rather than a collapsed point at 0).
const SketchRadar = ({ data }) => {
  const cx = 200, cy = 148, r = 92;
  const rings = [0.34, 0.67, 1];
  // v11: genuine zero (every axis at 0, not just low) skips the data shape
  // entirely — see the comment above vertex() for why. A real (non-zero)
  // value on even one axis brings it back automatically, since this is
  // recomputed on every render from whatever `data` actually is.
  const allZero = data.every((d) => d.value === 0);
  const dataPts = data.map((d, i) => vertex(i, d.value / 100, cx, cy, r));
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 340 }}>
      {/* v6: viewBox widened 340->400 (cx re-centered 170->200) — "TURNING
          POINT" (the longest axis label) grew rightward from its anchor
          point and exceeded the old 340-wide box, getting silently
          clipped by the svg's own overflow:hidden default ("TURNING PO").
          The extra width is pure margin for label text; the chart's own
          geometry (rings/polygon/dots, all still keyed off r=92) is
          unchanged, so this doesn't resize the visible chart itself. */}
      <svg viewBox="0 0 400 300" style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <radialGradient id="radarSoftGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={ACCENT} stopOpacity="0.28" />
            <stop offset="60%" stopColor={ACCENT} stopOpacity="0.10" />
            <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
          </radialGradient>
        </defs>

        {rings.map((frac, ri) => {
          const pts = data.map((_, i) => vertex(i, frac, cx, cy, r));
          const outer = ri === rings.length - 1;
          return <path key={ri} d={poly(pts)} fill={outer ? 'rgba(219,39,119,0.03)' : 'none'} stroke="rgba(219,39,119,0.22)" strokeWidth={outer ? 1.3 : 1.1} />;
        })}
        {data.map((_, i) => { const [vx, vy] = vertex(i, 1, cx, cy, r); return <line key={i} x1={cx} y1={cy} x2={vx} y2={vy} stroke="rgba(219,39,119,0.18)" strokeWidth={1} />; })}
        {!allZero && <path d={poly(dataPts)} fill="url(#radarSoftGrad)" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" />}
        {!allZero && dataPts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={3} fill={ACCENT} />)}
        {/* Axis labels — v9: always rendered at full opacity, no fade. The
            note card is a physical sheet of paper lying on top of the
            radar, not a thing that brings the radar into existence when it
            moves — the radar underneath is fully there the whole time.
            Sliding the sheet aside reveals it; the labels themselves never
            animate. (Previously faded in via `showLabels`, driven by
            NoteCoverCard's hover state — removed along with that wiring.)
            Coverage at rest is now the sheet's job entirely: see
            NoteCoverCard's sizing, tuned against this svg's real bounding
            box including these labels. */}
        {data.map((d, i) => {
          const [lx, ly, a] = vertex(i, 1.18, cx, cy, r);
          const cosA = Math.cos(a);
          const anchor = Math.abs(cosA) < 0.15 ? 'middle' : cosA > 0 ? 'start' : 'end';
          return (
            <text
              key={i}
              x={lx}
              y={ly}
              textAnchor={anchor}
              fontFamily="var(--font-mono)"
              fontSize="9.5"
              fontWeight="700"
              fill="hsl(var(--muted-foreground))"
              letterSpacing="0.03em"
            >
              {d.skill.toUpperCase()}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

/* ============================================================
   5. NOTE SHEET — easter egg over the skill radar
   v6: full redesign per Filip.
     - Paperclip deleted outright (was a lucide icon pinned to the top
       edge) — not restyled, gone.
     - v7: the torn edge is no longer a CSS clip-path guess — it's the
       actual alpha edge of paper-day.png/paper-night.png, rendered as a
       day/night <img> pair (same pattern as the castle/spool) sitting
       behind the text. The clip-path version leaked background pixels at
       a few notches for several rounds running; a real transparent PNG
       edge just doesn't have that failure mode.
     - Paper shadow is theme-aware (.story-note-sheet in index.css); dark
       mode gets a stronger, warmer drop-shadow plus a faint light rim so
       it still reads as an object sitting proud of a near-black page
       instead of a flat pasted-on rectangle (a dark shadow alone can't
       show up against an already-near-black background — there's no room
       left to darken). Text color also switches per theme via the
       --note-ink/--note-ink-muted custom properties defined alongside it,
       since night paper is dark enough that the day tones' dark-brown ink
       would read near-invisible on it.
     - Reveal is no longer CSS :hover/:focus-visible — it needed a "hold
       open ~1-2s after the trigger ends, then slide back" timer, which
       CSS can't express declaratively, plus an explicit tap path for
       touch (mobile Safari/Chrome's synthetic-hover-on-tap behavior is
       inconsistent enough to not rely on). State machine below: hover-in
       or focus-in reveals immediately and cancels any pending dismiss;
       hover-out or blur schedules a dismiss after NOTE_DISMISS_DELAY_MS;
       a tap/click reveals-and-schedules (or just re-schedules if already
       open); a pointerdown anywhere outside the sheet dismisses
       immediately, satisfying "tapping elsewhere should also dismiss it".
       Motion is a slide (translateY), not the old fade — "the radar is
       revealed because the paper moved off it" — with
       prefers-reduced-motion getting an instant cut, no transition. */
const NOTE_DISMISS_DELAY_MS = 1600; // "wait 1-2 seconds" — split the difference

// v13: named, hand-tunable drop distances — same pattern as the DATA_STORY_*
// constants above. v12's translateY(calc(100% + 40px)) used the CARD's own
// rendered height as the basis, which is the wrong number: the card is
// deliberately oversized versus the radar (so its torn edges clear the
// radar's outer ring/labels with margin at rest — see the card's own
// top/right/bottom/left insets below), so 100% of ITS height was more
// distance than the radar actually needs.
//
// IMPORTANT — this reduces the overshoot, it does not eliminate checklist
// coverage: at 1400px the card needs to travel ~297px to clear the radar,
// but the card itself is ~332px tall, while the gap between the radar's
// bottom edge and the checklist's top edge is only ~88px. 297 > 88, so ANY
// drop that fully clears the radar necessarily lands the card's own bottom
// edge well past the checklist's top — there is no drop distance that
// satisfies "clears the radar" and "never reaches the checklist"
// simultaneously, because the card is taller than the gap it would need to
// fit inside. Confirmed by direct measurement, not assumed. Raised with
// Filip; his call was to keep the card's current generous rest-state size
// (needed for full radar coverage at rest) and pick the SMALLEST drop that
// still fully clears the radar, so the checklist is covered for the
// minimum distance physically necessary rather than gratuitously more
// (v12's 372px overshot that minimum by 4x+).
// Measured directly (radar's own bounding box — heading + axis labels +
// outermost ring — vs. the card's rest position) rather than guessed:
//   - Desktop (3-column grid, common widths 1280-1440): needs ~265-297px to
//     clear the radar — 315 clears with a small margin.
//   - Mobile/stacked (<900px, single column): the radar column is capped at
//     max-width:340px and renders at a CONSTANT size across phone/tablet
//     widths, needing ~305px there regardless of viewport — 325 clears
//     with a small margin.
// Two constants, not one formula, because the radar's rendered size differs
// structurally between the 3-column grid and the single stacked column —
// not by a small amount a single number could split the difference on.
// KNOWN GAP: within the 3-column grid itself, the radar keeps shrinking as
// the grid narrows toward the 900px breakpoint (needs only ~111-161px to
// clear there), so DESKTOP's fixed 315 covers more of the checklist than
// the true minimum there — the same width band already flagged in earlier
// rounds as an inherently cramped 3-column squeeze. Not solved here; a
// third tier would be needed to tighten it, only worth adding if that width
// band matters in practice.
const NOTE_CARD_DROP_DISTANCE_DESKTOP = 300; // px, applied at >=900px (matches .story-hero-cols' own breakpoint)
const NOTE_CARD_DROP_DISTANCE_MOBILE = 300; // px, applied below 900px

const NoteCoverCard = () => {
  const [revealed, setRevealed] = useState(false);
  const dismissTimer = useRef(null);
  const cardRef = useRef(null);

  const clearDismissTimer = () => {
    if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
  };
  const reveal = () => { clearDismissTimer(); setRevealed(true); };
  const scheduleDismiss = () => {
    clearDismissTimer();
    dismissTimer.current = setTimeout(() => setRevealed(false), NOTE_DISMISS_DELAY_MS);
  };

  // v9: no longer reports `revealed` upward — the radar isn't hidden while
  // this is idle (see SketchRadar), so nothing outside this component
  // needs to know its state anymore.
  useEffect(() => clearDismissTimer, []);

  // Tapping/clicking elsewhere dismisses immediately (cancels the hold),
  // covering the "tapping elsewhere should also dismiss it" requirement
  // for touch — mouse users already get the same effect for free via
  // onMouseLeave's scheduled dismiss.
  useEffect(() => {
    if (!revealed) return undefined;
    const handleOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        clearDismissTimer();
        setRevealed(false);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [revealed]);

  const handleClick = (e) => {
    e.stopPropagation();
    reveal();
    scheduleDismiss();
  };
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handleClick(e);
  };

  return (
    <div
      ref={cardRef}
      className="story-note-cover story-note-sheet"
      tabIndex={0}
      role="button"
      aria-pressed={revealed}
      aria-label="Note: Great stories are designed, not discovered. Design yours. Hover, focus, or tap to slide it aside and reveal the skill radar underneath."
      onMouseEnter={reveal}
      onMouseLeave={scheduleDismiss}
      onFocus={reveal}
      onBlur={scheduleDismiss}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        position: 'absolute',
        // v8: was width:300/maxWidth:92% (fixed relative to this box's OWN
        // parent, not the radar) — visibly smaller than the radar
        // underneath, so its outer ring and axis labels poked out past
        // the paper's edges. A first pass at symmetric 130%-width/height
        // centered over the radar's box overcorrected: this column sits
        // close to the page top, so growing evenly in all four directions
        // pushed the sheet's top edge up into the navbar. Asymmetric
        // inset instead — small overshoot on top (little headroom there),
        // larger on the other three sides (room to spare there) — sized
        // against the radar's own wrapper box (see its aspect-ratio in
        // the JSX below, which gives that box a definite height so these
        // percentages have something real to resolve against). Verified
        // against the radar's actual measured bounding box, not eyeballed.
        // v9: top gets an extra fixed px on top of the percentage — the
        // "Skill radar · module completion" heading lives OUTSIDE this
        // wrapper (a plain-flow sibling above it, not part of the
        // aspect-ratio box), and now that it no longer fades it has to
        // stay fully covered at rest same as everything else. Its own
        // height doesn't scale with the column's width the way the radar
        // svg does, so a flat px add (not another percentage) is the
        // right unit here.
        top: 'calc(-8% - 30px)',
        right: '-14%',
        bottom: '-14%',
        left: '-14%',
        cursor: 'pointer',
        // v12: back to a slide, straight down — v11's peel (pivoting
        // around the sheet's own bottom-left corner) never actually
        // solved anything: a rotation around a corner sweeps the sheet's
        // FAR corner clear, but the chart sits under the sheet's CENTER,
        // which barely moves under a 12-15° pivot. Confirmed by hit-
        // testing a grid of points at full peel — only a sliver near one
        // corner was ever uncovered. A straight slide has no such blind
        // spot: it clears the whole sheet uniformly. Down is the one
        // direction with clearance at every breakpoint (desktop: castle
        // is beside this column, not above the slide path; stacked: the
        // castle is above, the checklist below — sliding down passes over
        // the checklist, which needs a z-index bump below, not a
        // direction change) so there's no per-breakpoint branch.
        // v13: was translateY(calc(100% + 40px)) — 100% resolved against
        // THIS element's own rendered height, which is the wrong basis:
        // the card is deliberately oversized versus the radar (see its
        // insets above), so 100% of its OWN height overshot well past what
        // the radar needs and landed on the checklist below. Replaced with
        // NOTE_CARD_DROP_DISTANCE_DESKTOP/MOBILE — named constants, hand-
        // measured against the radar's actual bounding box, not derived
        // from this element's own geometry (see their definitions above
        // NoteCoverCard for the full measurement and the one known gap).
        zIndex: 10,
        // Driven by React state directly (not a CSS .revealed class) —
        // a compound-class CSS rule here was computing to an inert
        // transform (verified via getComputedStyle: came back as the
        // identity matrix, matching neither the idle nor slid-away
        // value) for reasons that didn't resolve even after eliminating
        // calc(), duplicate rules, and stale-CSS theories one by one.
        // Inline style sidesteps the cascade question entirely — this is
        // the one property that changes with state, so it doesn't fight
        // the "inline beats class" trap that bit other parts of this
        // file in earlier rounds (nothing else needs to override it).
        transform: revealed
          ? `translateY(${(typeof window !== 'undefined' && window.innerWidth >= 900) ? NOTE_CARD_DROP_DISTANCE_DESKTOP : NOTE_CARD_DROP_DISTANCE_MOBILE}px)`
          : 'none',
      }}
    >
      <img
        src="/precision-storytelling/paper-day.png"
        alt=""
        className="story-note-paper-img story-note-paper-day"
        fetchPriority="high"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <img
        src="/precision-storytelling/paper-night.png"
        alt=""
        className="story-note-paper-img story-note-paper-night"
        fetchPriority="high"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div style={{
        position: 'relative', zIndex: 1, height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '0 34px', boxSizing: 'border-box',
      }}>
        <p style={{
          fontSize: 16,
          lineHeight: 1.6,
          color: 'var(--note-ink)',
          fontWeight: 500,
          fontFamily: 'var(--font-skill-story)',
          textAlign: 'center',
        }}>
          “Great stories are designed, not discovered. Design yours.”
        </p>
        <p style={{
          marginTop: 14,
          fontSize: 11,
          color: 'var(--note-ink-muted)',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          textAlign: 'center',
        }}>
          — Castle Atlas
        </p>
      </div>
    </div>
  );
};

// Name-reveal timing (badge click → letter-by-letter fade-in → hold → fade
// out). Kept as named constants so the hold/stagger durations are easy to
// retune without hunting through the JSX below.
const NAME_LETTER_STAGGER_MS = 45;
const NAME_LETTER_FADE_MS = 380;
const NAME_HOLD_MS = 3000;
const NAME_FADE_OUT_MS = 500;

/* ── Cloud module tile — idle float + hover/focus glass drawer ──
   `isOpen` (drawer visible) is owned by the parent so hovering/focusing a
   different tile can cancel this one's linger timer and close it immediately.
   The float pauses whenever the drawer is open (hover, focus, or the linger). */
const CloudTile = ({ mod, index, done, isOpen, onOpen, onScheduleClose, onActivate }) => {
  const complete = mod.reps > 0 && done >= mod.reps;
  const hideOnError = (e) => { e.currentTarget.style.display = 'none'; };
  const art = mod.hasArt;
  // Art fills the tile as its own visual. Oversized by 8px so the ±5px idle
  // float never reveals a gap at the clipped tile edge. Shrunk 10% uniformly
  // (all five modules, one mechanism — replaces the earlier one-off Pitch Arc
  // scale/position patch, which no longer exists in this file) so extremities
  // drawn close to the source art's edge (Pitch Arc's arrow, Callback's
  // boomerang trail) land inside the tile's clipped bounds instead of
  // bleeding past it. v6: shrunk another 10% on top of that (0.9*0.9=0.81)
  // per Filip — a fresh reduction from the current live size, not a restate
  // of the earlier pass. The 8px oversize buffer only needed to grow if the
  // art were scaling UP; shrinking further just leaves more slack, so it's
  // untouched. `flipHorizontal` (Callback only) mirrors the art so Echo's
  // boomerang throws away from Fletch's arrow rather than the same way.
  const artTransform = ['scale(0.81)', mod.flipHorizontal && 'scaleX(-1)'].filter(Boolean).join(' ');
  const artStyle = { position: 'absolute', inset: '-8px', width: 'calc(100% + 16px)', height: 'calc(100% + 16px)', objectFit: 'cover', transform: artTransform };
  // Badges get a frosted chip only over real art (they'd sit on a busy
  // illustration); over the flat placeholder cloud they stay plain.
  const badgeBase = { position: 'absolute', top: art ? 10 : '20%', fontSize: 10, fontWeight: 800 };

  // Name reveal — purely local to this tile (unlike the parent-owned drawer
  // state, nothing here needs cross-tile coordination). 'hidden' | 'shown' |
  // 'fading'. Clicking the badge again while already shown/fading just
  // restarts the hold timer from a fresh 'shown'.
  const [namePhase, setNamePhase] = useState('hidden');
  const nameTimers = useRef([]);
  const clearNameTimers = () => { nameTimers.current.forEach(clearTimeout); nameTimers.current = []; };
  useEffect(() => clearNameTimers, []);
  const revealName = () => {
    if (!mod.characterName) return;
    clearNameTimers();
    setNamePhase('shown');
    const revealTotal = mod.characterName.length * NAME_LETTER_STAGGER_MS + NAME_LETTER_FADE_MS;
    nameTimers.current.push(setTimeout(() => setNamePhase('fading'), revealTotal + NAME_HOLD_MS));
    nameTimers.current.push(setTimeout(() => setNamePhase('hidden'), revealTotal + NAME_HOLD_MS + NAME_FADE_OUT_MS));
  };
  // Isolated from the tile's hover-drawer and click-to-navigate behavior —
  // this only ever sets local reveal state, never bubbles to the tile.
  const handleBadgeClick = (e) => { e.stopPropagation(); revealName(); };
  const handleBadgeKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    revealName();
  };

  return (
    /* Fixed-footprint tile: a SQUARE the art fills edge-to-edge. The detail panel
       is an ABSOLUTE, bottom-anchored glass OVERLAY (see .story-panel in CSS), so
       opening it grows the overlay UP over the art and never changes the tile's
       own height — grid row heights stay fixed regardless of open state. Only the
       Start button navigates; role="group" + tabIndex give keyboard reveal.
         .story-tile (relative, aspect-ratio 1/1, overflow hidden)   ← fixed height
           ├─ .story-cloud-float  (absolute inset 0 — art fills tile; floats)
           │     ├─ art <img>s  (or placeholder <svg> cloud)
           │     ├─ number chip      (top-left, idle)
           │     └─ completion chip  (top-right, idle)
           └─ .story-panel        (ABSOLUTE bottom overlay; out of flow)
                 ├─ .story-panel-title   (idle "title chip" at the bottom)
                 └─ .story-panel-more    (reveals on hover, growing UP over art)
                       ├─ divider → description → count → Start */
    <div
      className="story-tile"
      data-open={isOpen ? 'true' : 'false'}
      role="group"
      tabIndex={0}
      aria-label={mod.title}
      onMouseEnter={onOpen}
      onMouseLeave={onScheduleClose}
      onFocus={onOpen}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) onScheduleClose(); }}
      style={{ position: 'relative', aspectRatio: '1 / 1', overflow: 'hidden', borderRadius: 14, outline: 'none' }}
    >
      {/* Art fills the whole tile; floats. Number + completion badges overlaid at rest. */}
      <div className="story-cloud-float" style={{ position: 'absolute', inset: 0, animationDelay: `${index * 0.6}s`, animationPlayState: isOpen ? 'paused' : 'running' }}>
        {art ? (
          <>
            {mod.characterImageDay && (
              <img className="story-char story-char-day" src={mod.characterImageDay} alt={mod.characterAlt || mod.title} onError={hideOnError} style={artStyle} />
            )}
            {mod.characterImageNight && (
              <img className="story-char story-char-night" src={mod.characterImageNight} alt={mod.characterAlt || mod.title} onError={hideOnError} style={artStyle} />
            )}
          </>
        ) : (
          <svg viewBox="0 0 240 120" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.12))' }} aria-hidden="true">
            <path d={CLOUD_PATHS[index % CLOUD_PATHS.length]} fill="var(--cloud-fill)" stroke="var(--cloud-stroke)" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        )}

        <span
          className={art ? 'story-chip' : undefined}
          role="button"
          tabIndex={0}
          aria-label={mod.characterName ? `Reveal ${mod.characterName}'s name` : undefined}
          onClick={handleBadgeClick}
          onKeyDown={handleBadgeKeyDown}
          style={{ ...badgeBase, left: 10, padding: art ? '2px 7px' : 0, fontFamily: 'var(--font-mono)', color: ACCENT, cursor: mod.characterName ? 'pointer' : undefined }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <span className={art ? 'story-chip' : undefined} style={{ ...badgeBase, right: 10, padding: art ? '2px 7px' : 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, letterSpacing: '0.04em', color: complete ? '#22c55e' : 'var(--cloud-ink-muted)' }}>
          {complete ? <><CheckCircle2 size={10} /> DONE</> : `${done}/${mod.reps}`}
        </span>

        {/* Character name reveal — triggered by the number badge above, not
            hover/click-to-navigate. Letters fade in left to right, hold, then
            the whole word fades out together (see NAME_* timing constants). */}
        {mod.characterName && namePhase !== 'hidden' && (
          <div className="story-name-reveal" style={{ opacity: namePhase === 'fading' ? 0 : 1 }}>
            {[...mod.characterName].map((ch, i) => (
              <span key={i} className="story-name-letter" style={{ animationDelay: `${i * NAME_LETTER_STAGGER_MS}ms` }}>
                {ch}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Glass overlay — absolute, anchored to the tile bottom (CSS). The title
          is the LAST child so it stays fixed in place; .story-panel-more is
          ordered ABOVE it and expands upward over the art on hover (never below
          the title, never resizing/obscuring it). Divider sits at the bottom of
          `more`, just above the fixed title. */}
      <div className="story-panel">
        <div className="story-panel-more" aria-hidden={!isOpen}>
          <p style={{ fontSize: 11, color: 'hsl(var(--foreground))', lineHeight: 1.45, margin: '0 0 7px' }}>{mod.description}</p>
          <div className="story-panel-row">
            <span style={{ fontSize: 10, fontWeight: 800, color: 'hsl(var(--foreground))' }}>{done}/{mod.reps} exercises</span>
            <button className="story-start" tabIndex={isOpen ? 0 : -1} onClick={onActivate}>
              {done > 0 ? MODULE_CTA[mod.slug]?.inProgress ?? 'Continue' : MODULE_CTA[mod.slug]?.notStarted ?? 'Start'} <ArrowRight size={11} />
            </button>
          </div>
          <div className="story-panel-divider" />
        </div>
        <h3 className="story-panel-title">{mod.title}</h3>
      </div>
    </div>
  );
};

const StorytellingLanding = () => {
  usePageTitle('Precision Storytelling | Castle Atlas');
  const navigate = useNavigate();
  const { completedExercises = [], loading } = useProgress();

  const byId = Object.fromEntries(MODULES.map((m) => [m.id, m]));
  const tiles = STORYTELLING_MODULES.map((sm) => ({ ...byId[sm.slug], ...sm }));

  const doneForLesson = (lessonId) => completedExercises.filter((e) => e.lesson_id === lessonId).length;

  // v12: permanent dev-only flag — ?preview=unlocked forces a plausible
  // fully(ish)-completed state through the radar, the arc/module-dots, the
  // stats row, and each module tile's CTA copy, so the page's "unlocked"
  // look can be reviewed without needing real progress data. Unlike a
  // scratch/TEMP flag this is a deliverable: kept permanently, but gated on
  // `import.meta.env.DEV` (Vite's dev-server flag, false in a production
  // build) so it's structurally impossible to trigger once built/deployed —
  // the query param is inert in prod regardless of what URL someone hits.
  // Also hides the radar's Locked stamp (see its own render below) and
  // switches `moduleStates`/CloudTile's `done` prop to "fully done" so the
  // whole page — radar shape, arc fill, milestone dots, stats, per-tile
  // buttons — reads as one coherent completed state rather than a mix of
  // real zeros and a fake radar. Computed before pct/moduleStates/radarData
  // below so all of them can read it. NOTE: ProtectedRoute's `if (!user)
  // return <Navigate to="/login" replace />` does not preserve the original
  // path or query string — if a tester's session isn't already live,
  // hitting this URL redirects through /login and silently drops
  // `?preview=unlocked` before it ever reaches this component; verify
  // through an already-authenticated session or a route that bypasses
  // ProtectedRoute.
  const previewUnlocked = import.meta.env.DEV
    && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('preview') === 'unlocked';

  const totalReps = tiles.reduce((s, m) => s + (m.reps || 0), 0);
  const storyRows = completedExercises.filter((e) => STORYTELLING_LESSON_IDS.includes(e.lesson_id));
  const doneReps = storyRows.length;
  const pct = previewUnlocked ? 100 : (totalReps > 0 ? Math.round((doneReps / totalReps) * 100) : 0);
  const hasStarted = previewUnlocked || doneReps > 0;

  // Stats strip — all from real completedExercises fields (`score` confirmed).
  const modulesStarted = previewUnlocked ? tiles.length : tiles.filter((m) => doneForLesson(m.lessonId) > 0).length;
  const scores = storyRows.map((e) => e.score).filter((s) => typeof s === 'number');
  const avgScore = previewUnlocked ? 96 : (scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null);

  // Per-module complete/incomplete, same formula CloudTile already uses —
  // feeds the arc's 5 subtle position dots (ordered Origin Cut → Callback).
  const moduleStates = previewUnlocked ? tiles.map(() => true) : tiles.map((m) => m.reps > 0 && doneForLesson(m.lessonId) >= m.reps);

  const radarData = tiles.map((m, i) => ({
    skill: shortTitle(m.title),
    value: previewUnlocked ? [90, 70, 100, 55, 80][i] : (m.reps > 0 ? Math.round((doneForLesson(m.lessonId) / m.reps) * 100) : 0),
  }));

  const goFirstModule = () => navigate(`${SKILL_ROUTE}/${STORYTELLING_MODULES[0].slug}`);

  // Which tile's drawer is open (parent-owned so a new hover can cancel another
  // tile's linger immediately). ~2.5s linger before a left tile retracts.
  const [openSlug, setOpenSlug] = useState(null);
  const lingerRef = useRef(null);
  const openTile = useCallback((slug) => {
    if (lingerRef.current) { clearTimeout(lingerRef.current); lingerRef.current = null; }
    setOpenSlug(slug);
  }, []);
  const scheduleClose = useCallback((slug) => {
    if (lingerRef.current) clearTimeout(lingerRef.current);
    lingerRef.current = setTimeout(() => {
      setOpenSlug((cur) => (cur === slug ? null : cur));
      lingerRef.current = null;
    }, 2500);
  }, []);
  useEffect(() => () => { if (lingerRef.current) clearTimeout(lingerRef.current); }, []);

  // Live-edit subheading: on first load, strike + collapse the filler tokens one
  // at a time until only the tight copy remains. Plays ONCE (never loops).
  const [struck, setStruck] = useState(() => new Set());
  const [cutOut, setCutOut] = useState(() => new Set());
  const [trimDone, setTrimDone] = useState(false);
  const trimRanRef = useRef(false);
  useEffect(() => {
    if (trimRanRef.current) return;
    trimRanRef.current = true;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setCutOut(new Set(SUBHEAD_CUTS));
      setTrimDone(true);
      return;
    }
    const timers = [];
    let delay = 850; // hold the rambling draft briefly before trimming
    SUBHEAD_CUTS.forEach((idx) => {
      timers.push(setTimeout(() => setStruck((s) => new Set(s).add(idx)), delay));
      timers.push(setTimeout(() => {
        setStruck((s) => { const n = new Set(s); n.delete(idx); return n; });
        setCutOut((s) => new Set(s).add(idx));
      }, delay + 200));
      delay += 240;
    });
    timers.push(setTimeout(() => setTrimDone(true), delay + 300));
    return () => timers.forEach(clearTimeout);
  }, []);

  /* Placed here rather than nearer the top of the component because every
     hook above has to run unconditionally: the trim-animation useEffect
     immediately above this is the last one, so this is the first point an
     early return is legal. `loading` is useProgress()'s flag, the same one
     the other skill landing pages gate on. */
  if (loading) {
    return (
      <SkillLoadingScreen
        iconType="art"
        daySrc={CASTLE_ICON_DAY}
        nightSrc={CASTLE_ICON_NIGHT}
        iconAlt="The Precision Storytelling castle"
        loadingText="storytelling"
      />
    );
  }

  const STATS = [
    { icon: CheckCircle2, label: 'Exercises cut', value: `${previewUnlocked ? totalReps : doneReps}` },
    { icon: Layers, label: 'Modules started', value: `${modulesStarted}/${tiles.length}` },
    { icon: BarChart3, label: 'Avg score', value: avgScore !== null ? `${avgScore}` : '—' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))', paddingBottom: 80, fontFamily: 'var(--font-body)' }}>
      {/* rail-clearance on this inner wrapper only — the cream background
          above must keep bleeding edge to edge under the floating rail;
          only the actual text/content gets pushed clear of it. */}
      <div className="rail-clearance">
      {/* ── Unified top block — manifesto, arc+castle, and radar as one
          combined section rather than a separate hero + bordered "overview"
          card below it, matching the reference mockups (none of them wrap
          the arc/radar in their own bordered panel — they sit on the same
          page background as the headline).
          v3: no more day/night sky gradient (removed per Filip — the hero
          now sits on the exact same hsl(var(--background)) as the rest of
          the page, set on the page root above, with no background of its
          own) and no more maxWidth/margin:auto centering — that centering
          was what made the hero text read as "a little left of true edge"
          instead of flush: at any viewport wider than ~1216px it left equal
          dead space on both sides of the 1160px cap, so the manifesto
          column started partway in rather than at the section's real left
          edge. Padding now matches the 24px the content below the hero
          uses, so the two sections' left edges land at the same pixel. ── */}
      <section className="story-rise" style={{ position: 'relative', padding: '36px 24px 32px' }}>
        <div className="story-hero-cols">
          {/* Left: manifesto — headline, subheading, CTA. */}
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', marginBottom: 14 }}>
              Communication · Precision Storytelling
            </div>
            <h1 style={{ fontFamily: 'var(--font-skill-story)', fontSize: 'clamp(2rem, 5vw, 3.1rem)', fontWeight: 800, color: 'hsl(var(--foreground))', letterSpacing: '-0.02em', lineHeight: 1.12, margin: '0 0 16px' }}>
              <span className="story-dropcap">T</span>ell the right story, tightly, so it lands <span className="story-onepoint">one point</span>.
            </h1>
            {/* Scissors sits above the subheading as its own line, not inline
                before the first word. Always mounted (never unmounted) so its
                box permanently reserves this space — only opacity toggles
                once the live-edit finishes, so the paragraph below never
                jumps up into space the icon used to occupy. */}
            <div
              className="story-snip-wrap"
              style={{ marginBottom: 6, opacity: trimDone ? 0 : 1 }}
              aria-hidden={trimDone}
            >
              <Scissors size={18} className="story-snip" style={{ color: ACCENT, animationPlayState: trimDone ? 'paused' : 'running' }} />
            </div>
            <p style={{ fontSize: 16, color: 'hsl(var(--muted-foreground))', lineHeight: 1.7, maxWidth: 600, margin: '0 0 26px' }}>
              {SUBHEAD_TOKENS.map((tok, i) => (
                <span key={i} className={`story-word${struck.has(i) ? ' striking' : ''}${cutOut.has(i) ? ' removed' : ''}`}>
                  {tok.t}
                </span>
              ))}
            </p>
            <button
              onClick={goFirstModule}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 26px', borderRadius: 14, border: 'none', cursor: 'pointer', background: ACCENT, color: '#fff', fontSize: 15, fontWeight: 800, boxShadow: '0 8px 24px rgba(219,39,119,0.35)' }}
            >
              {hasStarted ? `Continue · ${pct}%` : 'Start Writing'} <ArrowRight size={16} />
            </button>

            {/* v5: moved here from the middle column — with the heading
                block now top-aligned (see .story-hero-cols align-items),
                the manifesto column reads shorter than the castle/radar
                columns, leaving dead space below the button. Filling it
                with the stats strip instead of leaving it empty. */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 20 }}>
              {STATS.map(({ icon: Icon, label, value }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon size={14} color={ACCENT} />
                  <span style={{ fontSize: 14, fontWeight: 800, color: 'hsl(var(--foreground))' }}>{value}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Middle: castle above the arc, arc itself, caption below the
              arc (inside ArcProgress). v5: tightened gap (was relying on
              the castle's own margin-bottom:12px plus each child's own
              spacing) — a small explicit `gap` here replaces that so
              castle/arc/label/caption read as one compact stack instead of
              spread out. v8: this column is shorter than its siblings (the
              left column's manifesto text runs it down much further) and
              floated near the top of the shared row, disconnected from
              everything beside it. Tried full alignSelf:'end' (bottom-
              align against the row, landing "Arc Progress" exactly on the
              stats row's baseline) — measured out to a ~500-640px void
              above the castle, since the columns' natural heights are
              nowhere close, and no reasonable castle resize fills a gap
              that size. marginTop is the fallback: a smaller, fixed nudge
              downward, closer to center than to either edge, without
              chasing an exact baseline match that isn't achievable
              without leaving a void somewhere. Left/right columns are
              untouched (still governed by .story-hero-cols'
              align-items:start) — only this column gets the offset. On
              mobile/tablet, once .story-hero-cols collapses to a single
              stacked column, this offset serves no purpose (there's
              nothing to visually connect to anymore, all 3 stack in one
              flow) and just reads as a dead gap between the stats row
              above and the castle below — so the marginTop lives in CSS
              behind the same 900px breakpoint .story-hero-cols itself
              uses for the 3-column grid (.story-hero-middle-col), not as
              an inline style, specifically so it can be switched off
              below that width instead of applying unconditionally. */}
          <div className="story-hero-middle-col" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            {/* v11: castle rendering moved INSIDE ArcProgress — it's now
                one of three absolutely-positioned layers sharing that
                component's own geometry (see ArcProgress for why: castle
                size/position derive from the arc's own computed chord
                width and dip position, and doing that from a separate
                sibling div would mean re-deriving/duplicating that
                geometry here instead of having one source of truth). */}
            <ArcProgress pct={pct} moduleStates={moduleStates} />
          </div>

          {/* Right: skill radar, with the note card covering it (hover/focus
              easter egg — see NoteCoverCard), and the checklist + tagline
              (v4 — moved up from their own closing section further down,
              now that they sit right under the thing they're describing)
              beneath. v9: the radar is a physical object that's fully
              there at all times — the sheet is what's opaque, not the
              chart. Heading + axis labels no longer fade with the card's
              hover/focus state (that mechanism, and the radarRevealed
              state that drove it, are gone); coverage at rest is entirely
              the sheet's job now, sized to clear this heading too, not
              just the svg below it — see NoteCoverCard's top inset. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'hsl(var(--muted-foreground))', alignSelf: 'center', marginBottom: 4,
            }}>
              Skill radar · module completion
            </div>
            {/* aspectRatio matches SketchRadar's own viewBox (400x300) so
                this box's height is a definite CSS value derived from its
                width, not "auto" — NoteCoverCard sizes itself as a % of
                this same box, and percentage heights on an absolutely
                positioned element are ignored against an "auto"-height
                containing block. */}
            <div style={{ position: 'relative', width: '100%', maxWidth: 340, aspectRatio: '4 / 3' }}>
              <SketchRadar data={radarData} />
              {/* v12: back to centered, matching Sales & Negotiation's
                  reference (its own SkillRadar, `radarIsGhost` branch)
                  exactly — top:50%/left:50%/translate(-50%,-50%), rotate:
                  -4. v11 moved this into a corner because the peel reveal
                  it had at the time only ever cleared a sliver near the
                  sheet's own far corner, never the center. Now that the
                  reveal is a straight slide (see NoteCoverCard) that
                  clears the sheet uniformly off the whole chart, that
                  constraint is gone — centered is correct again, and
                  matches the reference precisely rather than working
                  around a mechanism that no longer exists. Same shared
                  components/StampBadge.jsx import as v10, not a
                  duplicate. No explicit z-index needed on the badge
                  itself: NoteCoverCard now carries an explicit z-index of
                  its own (10, so it can slide over the checklist below it
                  too — see NoteCoverCard), which already beats this
                  badge's default auto stacking level regardless of DOM
                  order, so the sheet covers it at rest and it's revealed
                  along with the rest of the radar once the sheet slides
                  down — it isn't a separate reveal, the sheet doesn't
                  know it's there. */}
              {/* Day/night color swap via CSS classes (not an isDark
                  ternary) — this file deliberately has no JS dark-mode
                  observer anywhere else (theming is CSS-only, `.dark` on
                  <html>), and StampBadge's color prop has to be a literal
                  hex string either way (it's string-concatenated into an
                  alpha suffix internally), so a CSS custom property
                  wouldn't work here regardless. */}
              {/* v12: hidden under ?preview=unlocked — the whole point of
                  that flag is to show what the page looks like once
                  there's real progress, which by definition isn't locked. */}
              {!previewUnlocked && (
                <>
                  <div className="story-locked-badge story-locked-badge-day" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                    <StampBadge color="#7a6b48" rotate={-4}>Locked</StampBadge>
                  </div>
                  <div className="story-locked-badge story-locked-badge-night" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                    <StampBadge color="#a89878" rotate={-4}>Locked</StampBadge>
                  </div>
                </>
              )}
              <NoteCoverCard />
            </div>

            <p style={{
              fontFamily: 'var(--font-skill-story)',
              fontSize: 'clamp(1.05rem, 1.8vw, 1.3rem)',
              fontWeight: 800,
              color: 'hsl(var(--foreground))',
              textAlign: 'center',
              margin: '18px 0 16px',
              lineHeight: 1.3,
            }}>
              The least detail that still lands the point.
            </p>

            {/* Handwritten-style checklist — a loose freehand check mark +
                each rubric item in the page's script face. Stacked (not the
                old wide auto-fit grid) since this column is much narrower
                than the closing section it used to live in. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', marginBottom: 2 }}>
                How your cut is scored
              </div>
              {RUBRIC.map((axis) => (
                <div key={axis} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="14" viewBox="0 0 16 14" style={{ flexShrink: 0 }} aria-hidden="true">
                    <path d="M 2 7 Q 5 11 6.5 11.5 Q 10 5 14 2" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ fontFamily: 'var(--font-script)', fontSize: 17, color: 'hsl(var(--foreground))' }}>{axis}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Full-width content, matching Copywriting/Sales/AI-Prompting (no page-level
          max-width cap — just horizontal padding). Was a narrow maxWidth:960 column. */}
      <div className="story-rise" style={{ padding: '0 24px', animationDelay: '120ms' }}>
        {/* ── Cloud module tiles — moved up to sit directly under the combined
            hero/overview block. Each tile now carries its own concept
            diagram directly beneath it (v3 — previously a separate row of
            5 diagrams below everything); "Raw ideas become stories that
            land." moved up here as this section's headline since the
            diagrams it introduces now live inside it. ── */}
        <section style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Layers size={15} color={ACCENT} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))' }}>The Modules</span>
          </div>
          <p style={{
            fontFamily: 'var(--font-skill-story)',
            fontSize: 'clamp(1.3rem, 3vw, 1.9rem)',
            fontWeight: 800,
            color: 'hsl(var(--foreground))',
            margin: '0 0 22px',
            lineHeight: 1.25,
          }}>
            Raw ideas become <span style={{ color: ACCENT, fontStyle: 'italic' }}>stories that land.</span>
          </p>
          {/* Spool + tile grid in one row — the spool is where the
              connecting thread visually spools out from. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <ThreadSpool />
            {/* align-items:start so hover-expanding one tile grows only that tile's
                box; row siblings keep their natural (collapsed) height rather than
                stretching. The row itself still gets taller while a tile is open —
                expected for in-flow hover-expand cards.
                Track upper bound is capped at 300px (not 1fr): the page has no
                max-width, so an unbounded 1fr track can grow to fill however much
                row space is left once `auto-fit` collapses to fewer columns — which
                is exactly what browser zoom triggers (Ctrl+/Ctrl- genuinely shrinks
                window.innerWidth, so zooming in drops column count same as a resize
                would). Since .story-tile is aspect-ratio:1/1 and the art fills it at
                100%, an unbounded track inflates the square tile — and the character
                art with it — instead of shrinking, once collapsed to one wide column.
                Capping the upper bound keeps tile/art size proportional to the page
                at every zoom level instead of "however wide this row happens to be."
                justifyContent:'center' (v3) — the tracks still cap at 300px each for
                that same zoom-safety reason, but once they hit that cap any leftover
                row width used to sit as dead space after the last column (grid's
                default justify-content:start). Centering distributes it evenly on
                both sides instead, so the row doesn't read as left-clustered — true
                edge-to-edge fill would mean uncapping the 300px tile size, which
                reintroduces the zoom bug the cap was added to fix. */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(255px, 300px))', justifyContent: 'center', gap: '18px 18px', alignItems: 'start' }}>
              {tiles.map((m, i) => (
                // alignItems:'stretch' (not 'center') — CloudTile has no
                // intrinsic width of its own; it relies on stretching to
                // fill this wrapper's width (which fills the grid track).
                // 'center' would shrink it to zero-width content sizing,
                // collapsing it via aspect-ratio. ConceptDiagram centers
                // its own content internally, so it isn't affected either
                // way.
                <div key={m.slug} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 16 }}>
                  <CloudTile
                    mod={m}
                    index={i}
                    done={previewUnlocked ? m.reps : doneForLesson(m.lessonId)}
                    isOpen={openSlug === m.slug}
                    onOpen={() => openTile(m.slug)}
                    onScheduleClose={() => scheduleClose(m.slug)}
                    onActivate={() => navigate(`${SKILL_ROUTE}/${m.slug}`)}
                  />
                  <ConceptDiagram slug={m.slug} />
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
      </div>
    </div>
  );
};

export default StorytellingLanding;